import React, { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Check, Plus, Calendar, Send, CheckCircle2, Filter, Sun } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, SectionHeader, Spinner, EmptyState, KPI } from "@/components/ui";
import { fmtMoney, fmtDayShort, mondayOf, weekDates } from "@/lib/dates";
import { addDays, format, parseISO, subDays } from "date-fns";

// =============================================================================
// Timesheets — day-to-day bread-and-butter screen
// =============================================================================
// Features:
//   · week picker with previous/next navigation
//   · role-aware filtering:
//       director/manager — all employees, filterable
//       supervisor       — only employees on projects they manage
//       field            — only their own timesheet
//   · click a day cell to add/edit hours inline (drawer)
//   · hours split (STD/TH/DT) computed by the database trigger — we just
//     show what comes back. Design doc 5.8 rules.
//   · approve column visible to manager+
//
// 22 Oct 2026 (mig 443) — James: "the team just need to see in their personal
// timesheet week view it's a public holiday and they will be paid, not just a
// blank day confusing everyone." A gazetted QLD/NATIONAL holiday reads amber in
// the header and, with nothing logged, the cell says "public holiday · paid"
// instead of "—". The 7.6 h PUB row is put on automatically two mornings after
// the holiday (api-gateway daily run → app.public_holiday_sweep); once it is
// there the cell shows the hours with a PH tag. Visual only on this screen —
// the manager override lives on HR → Team week.
// =============================================================================

interface TimesheetSummary {
  period_id: string;
  employee_id: string;
  employee_name: string;
  period_start: string;
  period_end: string;
  total_hours: number;
  total_std: number;
  total_th: number;
  total_dt: number;
  total_lafha_nights: number;
  total_lafha_amount: number;
  approved_entries: number;
  entry_count: number;
}

interface Entry {
  entry_id: string;
  employee_id: string;
  work_date: string;
  project_id: string | null;
  project_code?: string;
  total_hours: number;
  hours_std: number;
  hours_th: number;
  hours_dt: number;
  // T-324 — these MUST be loaded so the edit drawer initialises from the saved
  // values. Without them the drawer defaulted LAFHA→No / travel→blank and a
  // Save silently wiped lafha_nights/amount + travel on the existing row.
  lafha_nights: number;
  lafha_amount: number;
  travel_km: number | null;
  travel_hours: number | null;
  approved_at: string | null;
  submitted_at: string | null;
  notes: string | null;
  leave_type_code: string | null;    // mig 443 — 'PUB' = the paid public-holiday row
  leave_request_id: string | null;
}

export default function TimesheetsScreen() {
  const user = useRequireAuth();
  const [searchParams] = useSearchParams();
  // Allow deep-linking to a specific week (e.g. from /hr/timesheet-approvals).
  // Accepts ?week=YYYY-MM-DD; falls back to the current Monday.
  const initialWeek = useMemo(() => {
    const wk = searchParams.get("week");
    return wk && /^\d{4}-\d{2}-\d{2}$/.test(wk) ? wk : mondayOf();
  }, [searchParams]);
  const [weekStart, setWeekStart] = useState(initialWeek);
  // If the query param changes after mount (navigation within the app), sync
  // the week state so the deep-link always lands correctly.
  useEffect(() => { setWeekStart(initialWeek); }, [initialWeek]);
  const [showDrawer, setShowDrawer] = useState<{ date: string; entry?: Entry } | null>(null);
  const qc = useQueryClient();

  const canApprove = hasRole(user, "manager");
  // T-276 — /timesheets is the PERSONAL view for every role. Director/manager
  // team-wide approval + payroll prep live at /hr (HrHubScreen +
  // TimesheetApprovalsScreen). Hardcoding viewScope to "mine" filters
  // every query below to just the logged-in user's entries.
  const viewScope: "mine" | "team" | "all" = "mine";

  // -- fetch period ----------------------------------------------------------
  const { data: period, isLoading: loadingPeriod } = useQuery({
    queryKey: ["timesheet_period", weekStart],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheet_periods")
        .select("period_id, period_start, period_end, status")
        .eq("period_start", weekStart)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // -- weekly summary rows (one per employee) -------------------------------
  const { data: summary = [], isLoading: loadingSummary } = useQuery({
    queryKey: ["timesheet_summary", weekStart, viewScope, user?.employee_id],
    enabled: !!period,
    queryFn: async () => {
      let q = supabase
        .from("v_timesheet_weekly_summary")
        .select("*")
        .eq("period_id", period!.period_id);
      if (viewScope === "mine" && user?.employee_id) {
        q = q.eq("employee_id", user.employee_id);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data as TimesheetSummary[]) ?? [];
    },
  });

  // -- individual entries for the selected employee (or all if mgr+) --------
  const { data: entries = [], isLoading: loadingEntries } = useQuery({
    queryKey: ["timesheet_entries", weekStart, viewScope, user?.employee_id],
    enabled: !!period,
    queryFn: async () => {
      const dates = weekDates(weekStart);
      let q = supabase
        .from("timesheet_entries")
        .select(`
          entry_id, employee_id, work_date, project_id, total_hours,
          hours_std, hours_th, hours_dt, lafha_nights, lafha_amount,
          travel_km, travel_hours, approved_at, submitted_at, notes,
          leave_type_code, leave_request_id,
          projects(project_code)
        `)
        .gte("work_date", dates[0])
        .lte("work_date", dates[6]);
      if (viewScope === "mine" && user?.employee_id) {
        q = q.eq("employee_id", user.employee_id);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data as any[] ?? []).map(r => ({
        ...r,
        project_code: r.projects?.project_code,
      })) as Entry[];
    },
  });

  // mig 443 — the week's public holidays (same table the schedule paints from)
  const { data: holidays = [] } = useQuery({
    queryKey: ["timesheet_week_holidays", weekStart],
    queryFn: async () => {
      const ds = weekDates(weekStart);
      const { data, error } = await supabase
        .from("holidays")
        .select("holiday_date, holiday_name, state")
        .gte("holiday_date", ds[0])
        .lte("holiday_date", ds[6])
        .in("state", ["QLD", "NATIONAL"])
        .order("holiday_date");
      if (error) throw error;
      return (data ?? []) as { holiday_date: string; holiday_name: string; state: string }[];
    },
  });
  const holidayByDate = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of holidays) {
      const k = String(h.holiday_date).slice(0, 10);
      if (!m.has(k)) m.set(k, h.holiday_name);
    }
    return m;
  }, [holidays]);
  const weekdayHolidays = useMemo(
    () => Array.from(holidayByDate.entries()).filter(([d]) => { const dow = parseISO(d).getDay(); return dow >= 1 && dow <= 5; }),
    [holidayByDate],
  );

  const approveMutation = useMutation({
    mutationFn: async (entry_id: string) => {
      const { error } = await supabase
        .from("timesheet_entries")
        .update({ approved_at: new Date().toISOString(), approved_by_user_id: user!.user_id })
        .eq("entry_id", entry_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheet_entries"] });
      qc.invalidateQueries({ queryKey: ["timesheet_summary"] });
    },
  });

  const approveWeekMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const dates = weekDates(weekStart);
      const { error } = await supabase
        .from("timesheet_entries")
        .update({ approved_at: new Date().toISOString(), approved_by_user_id: user!.user_id })
        .eq("employee_id", employeeId)
        .gte("work_date", dates[0])
        .lte("work_date", dates[6])
        .is("approved_at", null);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheet_entries"] });
      qc.invalidateQueries({ queryKey: ["timesheet_summary"] });
    },
  });

  // T-256 — Lazy-create the period for the current week. Lets a director
  // "open" the week before any entries exist, so the grid renders empty
  // instead of the misleading "no period" empty state. RPC is idempotent
  // and open to any authenticated user (mig 037 grant). The MobileTimesheet
  // EntryScreen also calls this RPC on submit, so periods get created
  // either way — this just unblocks the desktop empty-state UX.
  const openWeekMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .schema("app")
        .rpc("ensure_timesheet_period", { p_date: weekStart });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheet_period"] });
    },
    onError: (e: any) => alert(e?.message ?? "Failed to open week"),
  });

  // Employee submits their own week — flips period to 'submitted' and stamps
  // submitted_at on every entry they own in this period. Director can still
  // approve/edit after. RPC lives in migration 037.
  const submitWeekMutation = useMutation({
    mutationFn: async () => {
      if (!period) throw new Error("No period for this week");
      const { error } = await supabase
        .schema("app")
        .rpc("submit_timesheet_week", { p_period_id: period.period_id });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheet_period"] });
      qc.invalidateQueries({ queryKey: ["timesheet_entries"] });
      qc.invalidateQueries({ queryKey: ["timesheet_summary"] });
    },
    onError: (e: any) => {
      alert(e?.message ?? "Failed to submit week");
    },
  });

  function confirmSubmitWeek() {
    if (!period) return;
    const weekLabel = `${format(parseISO(weekStart), "dd MMM")} – ${format(addDays(parseISO(weekStart), 6), "dd MMM yyyy")}`;
    if (!window.confirm(
      `Submit your timesheet for ${weekLabel}?\n\nThis notifies directors that your week is ready for approval. You can still edit entries after submitting, but try to have the week complete first.`
    )) return;
    submitWeekMutation.mutate();
  }

  // -- KPI roll-ups ---------------------------------------------------------
  const totals = useMemo(() => {
    const all = summary;
    return {
      people:   all.length,
      hours:    all.reduce((a, r) => a + (r.total_hours ?? 0), 0),
      ot:       all.reduce((a, r) => a + (r.total_th ?? 0) + (r.total_dt ?? 0), 0),
      lafha:    all.reduce((a, r) => a + (r.total_lafha_amount ?? 0), 0),
      nights:   entries.reduce((a, e) => a + (Number(e.lafha_nights) || 0), 0),
      approved: all.reduce((a, r) => a + (r.approved_entries ?? 0), 0),
      total_entries: all.reduce((a, r) => a + (r.entry_count ?? 0), 0),
    };
  }, [summary, entries]);

  const dates = weekDates(weekStart);
  const isApproved = period?.status === "approved" || period?.status === "locked";

  // My own submission state for the week (viewScope === "mine").
  // period.status flips to 'submitted' once ANY employee submits, so we look
  // at the per-entry submitted_at on the caller's own entries instead.
  const myEntries = useMemo(
    () => (user?.employee_id ? entries.filter(e => e.employee_id === user.employee_id) : []),
    [entries, user?.employee_id],
  );
  const mySubmitted = myEntries.length > 0 && myEntries.every(e => e.submitted_at);
  const myApproved  = myEntries.length > 0 && myEntries.every(e => e.approved_at);
  const canSubmitMyWeek =
    viewScope === "mine" && !!period && !myApproved && myEntries.length > 0;

  // -- group entries by employee + date for grid view ------------------------
  const gridData = useMemo(() => {
    const byEmp = new Map<string, { employee_name: string; cells: Record<string, Entry[]> }>();
    for (const r of summary) {
      byEmp.set(r.employee_id, { employee_name: r.employee_name, cells: {} });
    }
    for (const e of entries) {
      const emp = byEmp.get(e.employee_id);
      if (!emp) continue;
      (emp.cells[e.work_date] ||= []).push(e);
    }
    return Array.from(byEmp, ([id, v]) => ({ employee_id: id, ...v }));
  }, [summary, entries]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (!user) return null;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto pb-24 md:pb-6">
      {/* 24 Oct 2026 — phone polish (James, from Ugi's screenshot): one tidy
          header row, no Approved tile (approval is end of fortnight, the week
          pill already says where it's at), a day list instead of a sideways
          table on phones, and the OT rules folded away. Desktop unchanged
          apart from the tile. */}
      <div className="mb-4 md:mb-5 flex items-center justify-between flex-wrap gap-3">
        <div className="hidden md:block">
          <h1 className="text-2xl font-semibold tracking-tight">Timesheets</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Your time, this week. Team approvals + payroll live at{" "}
            <Link to="/hr" className="text-accent hover:underline">HR</Link>.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
          <div className="flex items-center gap-1 w-full md:w-auto">
            <Button variant="secondary" icon={ChevronLeft} size="sm" onClick={() => setWeekStart(format(subDays(parseISO(weekStart), 7), "yyyy-MM-dd"))}><span className="hidden md:inline">Prev</span></Button>
            <div className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md min-h-[40px]">
              <Calendar size={13} className="text-text-muted" />
              <span className="text-[13px] font-mono">
                {format(parseISO(weekStart), "dd MMM")} – {format(addDays(parseISO(weekStart), 6), "dd MMM yyyy")}
              </span>
              {period && <Pill label={period.status} tone={period.status === "approved" ? "approved" : "pending"} small />}
            </div>
            <Button variant="secondary" size="sm" onClick={() => setWeekStart(format(addDays(parseISO(weekStart), 7), "yyyy-MM-dd"))}>
              <span className="hidden md:inline">Next</span> <ChevronRight size={12} />
            </Button>
          </div>
          <Link to="/timesheets/new" className="flex-1 md:flex-none">
            <Button icon={Plus} size="sm" className="w-full md:w-auto">Log hours</Button>
          </Link>
          {canSubmitMyWeek && (
            mySubmitted ? (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-ok/10 border border-ok/20 rounded-md text-[12px] text-ok">
                <CheckCircle2 size={13} />
                Submitted · awaiting approval
              </div>
            ) : (
              <Button
                icon={Send}
                size="sm"
                onClick={confirmSubmitWeek}
                disabled={submitWeekMutation.isPending}
              >
                {submitWeekMutation.isPending ? "Submitting…" : "Submit my week"}
              </Button>
            )
          )}
        </div>
      </div>

      {/* mig 443 — the holiday is named before anyone looks at a blank cell */}
      {weekdayHolidays.length > 0 && (
        <div className="mb-4 p-3 rounded-md border border-warn/50 bg-warn/10 flex items-start gap-2.5">
          <Sun size={16} className="text-warn flex-shrink-0 mt-0.5" />
          <div className="text-[12px]">
            <div className="text-[13px] font-medium text-warn">
              {weekdayHolidays.map(([d, n]) => `${format(parseISO(d), "EEEE d MMM")} is ${n}`).join(" · ")} — public holiday
            </div>
            <div className="text-text-muted mt-0.5">
              Didn't work it? Leave the day empty — you're <span className="text-text">paid 7.6 h</span> for it and it's put on your timesheet automatically a couple of days after.
              Worked it? Log your hours as normal — every hour on a public holiday pays double time.
            </div>
          </div>
        </div>
      )}

      {loadingPeriod && (
        <div className="py-20 flex justify-center"><Spinner size={20} /></div>
      )}

      {!loadingPeriod && !period && (
        <Card>
          <div className="p-10 flex flex-col items-center text-center gap-4">
            <Calendar size={32} className="text-text-muted" />
            <div>
              <div className="text-text font-medium">No hours logged yet this week</div>
              <div className="text-[13px] text-text-muted mt-1 max-w-md">
                The weekly period is created automatically the first time
                someone logs hours. You can also open it now if you want to
                prep an empty grid for the team.
              </div>
            </div>
            <Button
              icon={Plus}
              onClick={() => openWeekMutation.mutate()}
              disabled={openWeekMutation.isPending}
            >
              {openWeekMutation.isPending ? "Opening…" : "Open this week"}
            </Button>
          </div>
        </Card>
      )}

      {period && (
        <>
          {/* KPI strip — T-276 dropped "People"; 24 Oct dropped "Approved"
              (approval happens end of fortnight — the week pill covers it). */}
          <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4 md:mb-5">
            <KPI label="Hours" value={totals.hours.toFixed(1)} sub={`${totals.total_entries} ${totals.total_entries === 1 ? "entry" : "entries"}`} />
            <KPI label="Overtime" value={`${totals.ot.toFixed(1)}h`} sub="TH + DT" accent={totals.ot > 40} />
            <KPI label="LAFHA" value={fmtMoney(totals.lafha)} sub={totals.nights > 0 ? `${totals.nights} night${totals.nights === 1 ? "" : "s"}` : undefined} />
          </div>

          {/* Phone: the week as a list of days, newest at the top is wrong for
              a timesheet — Monday first, like the paper one. Tap a day to log
              or edit it. */}
          <div className="md:hidden">
            {loadingSummary || loadingEntries ? (
              <div className="p-10 flex justify-center"><Spinner size={18} /></div>
            ) : (
              <Card>
                <div className="divide-y divide-line">
                  {dates.map(d => {
                    const cells = (gridData[0]?.cells[d] ?? []) as Entry[];
                    const hrs = cells.reduce((a, e) => a + (e.total_hours ?? 0), 0);
                    const nights = cells.reduce((a, e) => a + (Number(e.lafha_nights) || 0), 0);
                    const ot = cells.reduce((a, e) => a + (e.hours_th ?? 0) + (e.hours_dt ?? 0), 0);
                    const hol = holidayByDate.get(d);
                    const dow = parseISO(d).getDay();
                    const weekend = dow === 0 || dow === 6;
                    const pubRow = cells.find(e => e.leave_type_code === "PUB");
                    const leaveRow = !pubRow ? cells.find(e => e.leave_type_code) : undefined;
                    const worked = cells.filter(e => !e.leave_type_code);
                    const approved = cells.length > 0 && cells.every(e => e.approved_at);
                    const isToday = d === format(new Date(), "yyyy-MM-dd");
                    const tone = pubRow || (hol && worked.length > 0) ? "text-warn" : leaveRow ? "text-info" : approved ? "text-ok" : cells.length > 0 ? "text-text" : "text-text-dim";
                    return (
                      <button key={d} type="button" onClick={() => setShowDrawer({ date: d, entry: worked[0] ?? cells[0] })}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 active:bg-ink-raised ${hol ? "bg-warn/8" : ""}`}>
                        <div className="w-12 flex-shrink-0">
                          <div className={`text-[12px] font-medium ${isToday ? "text-accent" : weekend ? "text-text-muted" : "text-text"}`}>{fmtDayShort(d)}</div>
                          <div className="text-[11px] font-mono text-text-dim">{format(parseISO(d), "dd/MM")}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          {pubRow ? (
                            <div className="text-[12px] text-warn">{hol ?? "Public holiday"} · paid 7.6 h</div>
                          ) : leaveRow ? (
                            <div className="text-[12px] text-info">{leaveRow.leave_type_code}{(leaveRow.total_hours ?? 0) > 0 ? " · paid leave" : " · unpaid"}</div>
                          ) : worked.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              {worked.map(c => <span key={c.entry_id} className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-ink-raised border border-line text-text-muted">{c.project_code ?? "—"}{worked.length > 1 ? ` ${(c.total_hours ?? 0).toFixed(1)}` : ""}</span>)}
                              {nights > 0 && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-accent/12 text-accent">TA{nights > 1 ? ` ×${nights}` : ""}</span>}
                              {hol && <span className="text-[10px] px-1.5 py-0.5 rounded bg-warn/15 text-warn">{hol} · all DT</span>}
                            </div>
                          ) : hol && !weekend ? (
                            <div className="text-[12px] text-warn">{hol} · paid, nothing to log</div>
                          ) : (
                            <div className="text-[12px] text-text-dim">{weekend ? "—" : "tap to log"}</div>
                          )}
                          {ot > 0 && !pubRow && <div className="text-[10px] font-mono text-warn mt-0.5">{ot.toFixed(1)}h overtime</div>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className={`font-mono text-[18px] leading-none ${tone}`}>{cells.length > 0 ? hrs.toFixed(1) : ""}</div>
                          {approved && <div className="text-[9px] uppercase tracking-wider text-ok mt-1">approved</div>}
                        </div>
                        <ChevronRight size={14} className="text-text-dim flex-shrink-0"/>
                      </button>
                    );
                  })}
                </div>
                <div className="px-4 py-2.5 border-t border-line flex items-center justify-between text-[12px]">
                  <span className="text-text-muted">Week total</span>
                  <span className="font-mono text-text">{totals.hours.toFixed(1)} h{totals.ot > 0 ? <span className="text-warn"> · {totals.ot.toFixed(1)} OT</span> : null}</span>
                </div>
              </Card>
            )}
          </div>

          {/* The grid (desktop) */}
          <Card className="hidden md:block">
            {loadingSummary || loadingEntries ? (
              <div className="p-10 flex justify-center"><Spinner size={18} /></div>
            ) : gridData.length === 0 ? (
              <EmptyState title="No time logged yet" description="Get started by adding your first entry." icon={Plus} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left px-4 py-3 text-[10px] font-medium tracking-wider2 uppercase text-text-muted sticky left-0 bg-ink-card z-[1]">Employee</th>
                      {dates.map(d => {
                        const hol = holidayByDate.get(d);   // mig 443
                        return (
                          <th key={d} className={`text-center px-2 py-3 text-[10px] font-medium tracking-wider2 uppercase text-text-muted min-w-[90px] ${hol ? "bg-warn/15 shadow-[inset_0_-2px_0_rgba(234,179,8,0.85)]" : ""}`}
                              title={hol ? `Public holiday — ${hol}` : undefined}>
                            <div>{fmtDayShort(d)}</div>
                            <div className="font-mono text-text-dim mt-0.5">{format(parseISO(d), "dd/MM")}</div>
                            {hol && <div className="normal-case tracking-normal text-warn mt-1 leading-tight">{hol}</div>}
                          </th>
                        );
                      })}
                      <th className="text-right px-4 py-3 text-[10px] font-medium tracking-wider2 uppercase text-text-muted">Total</th>
                      {/* T-276 — Approve column only on team/all views. Personal
                          Timesheets view doesn't show Approve buttons; HR Hub
                          (TimesheetApprovalsScreen) is the canonical place to
                          approve. Stops managers self-approving from /timesheets. */}
                      {canApprove && viewScope !== "mine" && <th className="px-4 py-3 text-[10px] font-medium tracking-wider2 uppercase text-text-muted"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {gridData.map((row, idx) => {
                      const s = summary.find(s => s.employee_id === row.employee_id);
                      const weekHours = s?.total_hours ?? 0;
                      const weekOt    = (s?.total_th ?? 0) + (s?.total_dt ?? 0);
                      const unapproved = dates.reduce((n, d) =>
                        n + (row.cells[d]?.some(e => !e.approved_at) ? 1 : 0), 0);
                      return (
                        <tr key={row.employee_id} className={`border-b border-line ${idx % 2 ? "bg-ink/40" : ""}`}>
                          <td className="px-4 py-3 sticky left-0 bg-inherit z-[1]">
                            <div className="text-text">{row.employee_name}</div>
                            {weekOt > 0 && <div className="text-[11px] text-warn font-mono">{weekOt.toFixed(1)}h OT</div>}
                          </td>
                          {dates.map(d => {
                            const cells = row.cells[d] ?? [];
                            const hrs = cells.reduce((a, e) => a + (e.total_hours ?? 0), 0);
                            const nights = cells.reduce((a, e) => a + (Number(e.lafha_nights) || 0), 0); // T-325
                            const allApproved = cells.length > 0 && cells.every(e => e.approved_at);
                            // mig 443 — a public holiday: the paid PUB row once it has landed,
                            // "public holiday · paid" while the cell is still empty, and
                            // worked hours on it in amber (all double time).
                            const hol = holidayByDate.get(d);
                            const pubRow = cells.find(e => e.leave_type_code === "PUB");
                            const leaveRow = !pubRow ? cells.find(e => e.leave_type_code) : undefined;
                            const workedOnHol = !!hol && cells.some(e => !e.leave_type_code && (e.total_hours ?? 0) > 0);
                            return (
                              <td key={d} className={`px-2 py-2 text-center ${hol ? "bg-warn/8" : ""}`}>
                                {pubRow ? (
                                  <div className="w-full py-1.5 rounded text-[13px] font-mono bg-warn/15 text-warn"
                                       title={`Public holiday — ${hol ?? "paid"} · ${(pubRow.total_hours ?? 0).toFixed(1)} h ordinary, paid`}>
                                    {hrs.toFixed(1)}
                                    <div className="text-[9px] font-sans leading-none mt-0.5">public holiday · paid</div>
                                  </div>
                                ) : leaveRow ? (
                                  <div className="w-full py-1.5 rounded text-[13px] font-mono bg-info/12 text-info"
                                       title={leaveRow.notes ?? leaveRow.leave_type_code ?? "leave"}>
                                    {hrs.toFixed(1)}
                                    <div className="text-[9px] font-sans leading-none mt-0.5">{leaveRow.leave_type_code}{(leaveRow.total_hours ?? 0) > 0 ? " · paid leave" : " · unpaid"}</div>
                                  </div>
                                ) : cells.length > 0 ? (
                                  <button
                                    onClick={() => setShowDrawer({ date: d, entry: cells[0] })}
                                    className={`w-full py-1.5 rounded text-[13px] font-mono transition-colors ${
                                      workedOnHol
                                        ? "bg-warn/20 text-warn hover:bg-warn/30"
                                        : allApproved
                                        ? "bg-ok/10 text-ok hover:bg-ok/20"
                                        : "bg-ink-raised text-text hover:bg-ink-hover"
                                    }`}
                                    title={(workedOnHol ? `Worked on ${hol} — every hour double time\n` : "") + cells.map(c => `${c.project_code ?? "—"}: ${c.total_hours}h${(Number(c.lafha_nights) || 0) > 0 ? " · TA night" : ""}`).join("\n")}
                                  >
                                    {hrs.toFixed(1)}
                                    {nights > 0 && (
                                      <div className="text-[9px] text-accent font-mono leading-none mt-0.5">
                                        TA{nights > 1 ? `·${nights}` : ""}
                                      </div>
                                    )}
                                  </button>
                                ) : hol && parseISO(d).getDay() >= 1 && parseISO(d).getDay() <= 5 ? (
                                  <button
                                    onClick={() => setShowDrawer({ date: d })}
                                    className="w-full py-1.5 rounded text-[11px] text-warn bg-warn/10 border border-warn/30 hover:bg-warn/20 leading-tight"
                                    title={`${hol} — public holiday. You're paid 7.6 h; it's put on your timesheet automatically. Only log hours here if you actually worked.`}
                                  >
                                    public holiday<div className="text-[9px] opacity-80">paid · nothing to log</div>
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setShowDrawer({ date: d })}
                                    className="w-full py-1.5 rounded text-text-dim text-[13px] hover:bg-ink-hover hover:text-text-muted"
                                  >
                                    —
                                  </button>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-4 py-3 text-right font-mono text-text">{weekHours.toFixed(1)}</td>
                          {/* T-276 — see column header note above. */}
                          {canApprove && viewScope !== "mine" && (
                            <td className="px-4 py-3">
                              {unapproved > 0 ? (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  icon={Check}
                                  onClick={() => approveWeekMutation.mutate(row.employee_id)}
                                  disabled={approveWeekMutation.isPending}
                                >
                                  Approve
                                </Button>
                              ) : (
                                <Pill label="approved" tone="approved" small />
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Legend (desktop) */}
          <div className="mt-4 hidden md:flex items-center gap-4 text-[11px] text-text-muted font-mono">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-ok/20" /> Approved</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-ink-raised border border-line" /> Logged, pending</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded border border-line-light" /> Empty</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-warn/20" /> Public holiday (paid 7.6 h, or worked = all DT)</span>
            <span className="ml-auto">OT rules: weekday 0–8 STD · 8–10 TH · 10+ DT · Sat first 2 TH, rest DT · Sun/PH all DT</span>
          </div>
          {/* Phone: one line, folded */}
          <details className="md:hidden mt-3 text-[12px] text-text-muted">
            <summary className="cursor-pointer select-none py-2 min-h-[40px] flex items-center">How overtime works</summary>
            <div className="px-1 pb-2 space-y-1">
              <div><span className="text-text">Weekdays:</span> first 8 h ordinary · next 2 h time-and-a-half · after 10 h double time</div>
              <div><span className="text-text">Saturday:</span> first 2 h time-and-a-half, the rest double time</div>
              <div><span className="text-text">Sunday &amp; public holidays:</span> all double time</div>
              <div><span className="text-text">Public holiday not worked:</span> paid 7.6 h, added for you — nothing to log</div>
              <div>Approval happens at the end of the fortnight, not day by day.</div>
            </div>
          </details>

          {/* T-276 — "Prepare pay run" button removed. Lives at HR Hub →
              Payroll summary now (director-only there too). */}
        </>
      )}

      {showDrawer && (
        <EntryDrawer
          date={showDrawer.date}
          entry={showDrawer.entry}
          periodId={period?.period_id}
          viewerEmployeeId={user.employee_id ?? null}
          onClose={() => setShowDrawer(null)}
          canApprove={canApprove}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["timesheet_entries"] });
            qc.invalidateQueries({ queryKey: ["timesheet_summary"] });
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Drawer: add/edit a single day's entry
// =============================================================================
function EntryDrawer({ date, entry, periodId, viewerEmployeeId, onClose, canApprove, onSaved }: {
  date: string;
  entry?: Entry;
  periodId?: string;
  viewerEmployeeId: string | null;
  canApprove: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [hours, setHours] = useState(entry?.total_hours?.toString() ?? "8");
  const [projectId, setProjectId] = useState<string | null>(entry?.project_id ?? null);
  const [notes, setNotes] = useState(entry?.notes ?? "");
  // T-316 — fields previously missing from the edit drawer. Wizard at
  // /timesheets/new has them, drawer didn't, so any field forgotten at
  // first entry was unrecoverable via the UI. Now editable here too.
  const [lafhaAway,   setLafhaAway]   = useState<boolean>(((entry as any)?.lafha_nights ?? 0) > 0);
  const [travelKm,    setTravelKm]    = useState<string>(
    (entry as any)?.travel_km != null ? String((entry as any).travel_km) : "",
  );
  const [travelHours, setTravelHours] = useState<string>(
    (entry as any)?.travel_hours != null ? String((entry as any).travel_hours) : "",
  );
  const [saving, setSaving] = useState(false);

  const { data: projects = [] } = useQuery({
    queryKey: ["project_options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name")
        .in("status", ["live", "mobilising", "acceptance_pending"])
        .order("project_code");
      if (error) throw error;
      return data;
    },
  });

  // T-316 — load the entry's employee context for LAFHA eligibility + rate.
  // The drawer is shown both for new entries (uses viewerEmployeeId) and for
  // existing ones (entry.employee_id). Need this to gate the LAFHA toggle.
  const employeeId = entry?.employee_id ?? viewerEmployeeId ?? null;
  const { data: employeeCtx } = useQuery({
    queryKey: ["entry_drawer_employee", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, lafha_eligible, lafha_rate")
        .eq("employee_id", employeeId!)
        .single();
      if (error) throw error;
      return data as { employee_id: string; lafha_eligible: boolean | null; lafha_rate: number | null };
    },
  });
  const lafhaEligible = !!employeeCtx?.lafha_eligible;
  const lafhaRate     = Number(employeeCtx?.lafha_rate ?? 250);

  async function save() {
    if (!periodId || !viewerEmployeeId) return;
    setSaving(true);
    try {
      // T-316 — LAFHA + travel field write-through
      const lafhaNights = lafhaEligible && lafhaAway ? 1 : 0;
      const lafhaAmount = lafhaNights > 0 ? lafhaRate : 0;
      const travelKmNum    = travelKm.trim()    === "" ? null : Math.max(0, parseFloat(travelKm)    || 0);
      const travelHoursNum = travelHours.trim() === "" ? null : Math.max(0, parseFloat(travelHours) || 0);

      const row = {
        period_id: periodId,
        employee_id: entry?.employee_id ?? viewerEmployeeId,
        work_date: date,
        project_id: projectId,
        total_hours: parseFloat(hours) || 0,
        notes: notes || null,
        lafha_nights: lafhaNights,
        lafha_amount: lafhaAmount,
        travel_km:    travelKmNum,
        travel_hours: travelHoursNum,
      };
      if (entry) {
        const { error } = await supabase.from("timesheet_entries").update(row).eq("entry_id", entry.entry_id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("timesheet_entries").insert(row);
        if (error) throw error;
      }
      onSaved();
      onClose();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center" onClick={onClose}>
      <div className="bg-ink-card border-t md:border border-line w-full md:max-w-md md:rounded-lg p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{format(parseISO(date), "EEEE dd MMM")}</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text">×</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">Project</label>
            <select
              value={projectId ?? ""}
              onChange={e => setProjectId(e.target.value || null)}
              className="mt-1.5 w-full px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm outline-none focus:border-accent"
            >
              <option value="">— none —</option>
              {projects.map((p: any) => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_code} · {p.project_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">Hours</label>
            <input
              type="number" step="0.25" min="0" max="24" value={hours}
              onChange={e => setHours(e.target.value)}
              className="mt-1.5 w-full px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm font-mono outline-none focus:border-accent"
            />
            <div className="text-[11px] text-text-muted mt-1.5">
              Split (STD / TH / DT) is computed automatically when you save.
            </div>
          </div>

          {/* T-316 — LAFHA toggle (only for eligible employees) */}
          {lafhaEligible && (
            <div>
              <label className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">
                Away from home (LAFHA)
              </label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setLafhaAway(false)}
                  className={`px-3 py-2 rounded-md text-[13px] border transition-colors ${
                    !lafhaAway
                      ? "bg-accent/15 border-accent text-accent"
                      : "bg-ink-raised border-line text-text hover:border-line-light"
                  }`}
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={() => setLafhaAway(true)}
                  className={`px-3 py-2 rounded-md text-[13px] border transition-colors ${
                    lafhaAway
                      ? "bg-accent/15 border-accent text-accent"
                      : "bg-ink-raised border-line text-text hover:border-line-light"
                  }`}
                >
                  Yes · ${lafhaRate.toFixed(0)}
                </button>
              </div>
            </div>
          )}

          {/* T-316 — Travel km + hours (tracking only — not added to total_hours) */}
          <div>
            <label className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">
              Travel (optional)
            </label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <input
                type="number" step="0.1" min="0" inputMode="decimal"
                value={travelKm}
                onChange={e => setTravelKm(e.target.value)}
                placeholder="Round-trip km"
                className="px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm font-mono outline-none focus:border-accent"
              />
              <input
                type="number" step="0.25" min="0" inputMode="decimal"
                value={travelHours}
                onChange={e => setTravelHours(e.target.value)}
                placeholder="Travel hours"
                className="px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm font-mono outline-none focus:border-accent"
              />
            </div>
            <div className="text-[11px] text-text-muted mt-1.5">
              Tracking only — doesn't change your day total. Pay is based on the hours above.
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">Notes</label>
            <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Optional — what you did, anything unusual"
              className="mt-1.5 w-full px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm outline-none focus:border-accent resize-y"
            />
          </div>

          {entry?.approved_at && (
            <div className="bg-ok/10 border border-ok/20 rounded px-3 py-2 text-[12px] text-ok">
              Approved — edits will unapprove this entry.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} icon={CheckCircle2}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}
